import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";import {
  ArrowLeft, Plus, Users, Copy, X, ZoomIn, ZoomOut, Locate, Trash2,
  UserPlus, Heart, ChevronUp, ChevronDown, Save, Edit3, Flame,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { layoutTree } from "@/lib/treeLayout";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import MultilingualText from "@/components/ui/MultilingualText";
import PhotoDropzone from "@/components/ui/PhotoDropzone";
import { packMl } from "@/lib/ml";
import { toast } from "sonner";

const emptyPerson = {
  first_name: "",
  middle_name: "",
  last_name: "",
  maiden_name: "",
  gender: "unknown",
  birth_date: "",
  birth_place: "",
  death_date: "",
  death_place: "",
  is_living: true,
  bio: "",
  photo_url: "",
};

export default function FamilyWorkspace() {
  const { id } = useParams();
  const [family, setFamily] = useState(null);
  const [persons, setPersons] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // Add-person modal: { anchor: personId | null, mode: 'orphan'|'parent'|'child'|'spouse' }
  const [addModal, setAddModal] = useState(null);
  const [editing, setEditing] = useState(false); // when true, side panel is in edit mode

  const load = useCallback(async () => {
    try {
      const [famRes, pRes, rRes] = await Promise.all([
        api.get(`/v1/families/${id}`),
        api.get(`/v1/persons`, { params: { family_id: id } }),
        api.get(`/v1/relationships`, { params: { family_id: id } }),
      ]);
      setFamily(famRes.data.family);
      setPersons(pRes.data.persons || []);
      setRelationships(rRes.data.relationships || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const layout = useMemo(() => layoutTree(persons, relationships), [persons, relationships]);
  const selected = selectedId ? layout.nodes[selectedId] || persons.find((p) => p.id === selectedId) : null;

  // ---------- Actions ----------
  async function createPerson(data, relSpec) {
    try {
      const { data: pdata } = await api.post("/v1/persons", { family_id: id, ...data });
      const newId = pdata.person.id;
      if (relSpec) {
        // relSpec: { type: 'parent'|'spouse', from, to } — parent means from IS parent OF to
        try {
          await api.post("/v1/relationships", {
            family_id: id,
            type: relSpec.type,
            from_person_id: relSpec.from === "__new__" ? newId : relSpec.from,
            to_person_id: relSpec.to === "__new__" ? newId : relSpec.to,
          });
        } catch (e) {
          toast.error("Person created, but relationship failed: " + formatApiError(e));
        }
      }
      toast.success("Person added.");
      setSelectedId(newId);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function updatePerson(personId, patch) {
    try {
      await api.patch(`/v1/persons/${personId}`, patch);
      toast.success("Saved.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deletePerson(personId) {
    if (!confirm("Delete this person and all their connections?")) return;
    try {
      await api.delete(`/v1/persons/${personId}`);
      toast.success("Removed.");
      if (selectedId === personId) setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function copyInvite() {
    if (family?.invite_code) {
      navigator.clipboard
        .writeText(family.invite_code)
        .then(() => toast.success("Invite code copied."))
        .catch(() => toast.error("Copy failed — please copy it manually."));
    }
  }

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 text-sm text-[color:var(--ink-soft)]">
        Loading family archive…
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-[1600px] px-4 lg:px-8 pt-8 pb-4">
      {/* Header strip */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <Link
            to="/app"
            data-testid="workspace-back"
            className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-soft)] link-underline mb-2"
          >
            <ArrowLeft size={12} /> All archives
          </Link>
          <h1
            data-testid="workspace-family-name"
            className="font-serif-display text-3xl md:text-5xl leading-[0.95] tracking-tight"
          >
            {family?.name}
          </h1>
          {family?.description && (
            <p className="mt-2 text-sm text-[color:var(--ink-soft)] max-w-2xl">
              {family.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-2 text-[color:var(--ink-soft)]">
            <Users size={14} /> {(family?.members || []).length} member
            {(family?.members || []).length !== 1 ? "s" : ""}
          </span>
          <button
            data-testid="workspace-copy-invite"
            onClick={copyInvite}
            className="inline-flex items-center gap-2 border border-[color:var(--ink-line-strong)] px-3 py-2 hover:bg-[color:var(--bone-2)]"
            title="Invite code — click to copy"
          >
            <Copy size={12} /> {family?.invite_code}
          </button>
          <button
            data-testid="workspace-add-person-btn"
            onClick={() => setAddModal({ anchor: null, mode: "orphan" })}
            className="btn-rust !py-2 !px-4"
          >
            <Plus size={14} /> Add person
          </button>
          <Link
            to={`/app/family/${id}/feed`}
            data-testid="workspace-open-feed"
            className="btn-ghost !py-2 !px-4"
          >
            Family feed →
          </Link>
          <Link
            to={`/app/family/${id}/council`}
            data-testid="workspace-open-council"
            className="btn-ghost !py-2 !px-4"
          >
            Ask the council →
          </Link>
        </div>
      </div>

      {/* Main split: canvas + side panel */}
      <div className="grid lg:grid-cols-[1fr_360px] gap-0 border border-[color:var(--ink-line)] min-h-[68vh]">
        {/* Canvas */}
        <TreeCanvas
          layout={layout}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onAdd={(mode, anchor) => setAddModal({ anchor, mode })}
          personsCount={persons.length}
        />

        {/* Side panel */}
        <aside
          data-testid="workspace-side-panel"
          className="border-t lg:border-t-0 lg:border-l border-[color:var(--ink-line)] bg-[color:var(--bone-2)]/40 min-h-[300px]"
        >
          {selected ? (
            <PersonPanel
              key={selected.id}
              person={selected}
              editing={editing}
              setEditing={setEditing}
              onSave={(patch) => updatePerson(selected.id, patch)}
              onDelete={() => deletePerson(selected.id)}
              onAdd={(mode) => setAddModal({ anchor: selected.id, mode })}
              relationships={relationships}
              persons={persons}
            />
          ) : (
            <div className="p-8 text-sm text-[color:var(--ink-soft)]">
              <div className="overline mb-3">The reading room</div>
              <p className="font-italic-serif italic text-lg text-[color:var(--ink)] leading-snug">
                Select a person from the tree to see their record, or add the first name to begin your archive.
              </p>
              {persons.length === 0 && (
                <button
                  data-testid="workspace-first-person-btn"
                  onClick={() => setAddModal({ anchor: null, mode: "orphan" })}
                  className="btn-rust mt-6"
                >
                  <Plus size={14} /> Add the first person
                </button>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Add person modal */}
      <AnimatePresence>
        {addModal && (
          <AddPersonModal
            key={addModal.anchor + ":" + addModal.mode}
            spec={addModal}
            anchorPerson={addModal.anchor ? persons.find((p) => p.id === addModal.anchor) : null}
            onClose={() => setAddModal(null)}
            onCreate={async (data, relSpec) => {
              await createPerson(data, relSpec);
              setAddModal(null);
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

/* ---------------- Tree Canvas ---------------- */

function TreeCanvas({ layout, selectedId, onSelect, onAdd, personsCount }) {
  const { nodes, edges, size, NODE_W, NODE_H } = layout;
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0.9);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef({ dragging: false, x: 0, y: 0, sx: 0, sy: 0 });

  function onWheel(e) {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScale((s) => Math.max(0.3, Math.min(1.6, s + delta)));
  }
  function onMouseDown(e) {
    if (e.target.closest("[data-node]")) return;
    dragRef.current = { dragging: true, x: e.clientX, y: e.clientY, sx: tx, sy: ty };
  }
  function onMouseMove(e) {
    if (!dragRef.current.dragging) return;
    setTx(dragRef.current.sx + (e.clientX - dragRef.current.x));
    setTy(dragRef.current.sy + (e.clientY - dragRef.current.y));
  }
  function onMouseUp() {
    dragRef.current.dragging = false;
  }

  function fit() {
    setScale(0.9);
    setTx(0);
    setTy(0);
  }

  // Build connector paths
  const parentEdges = edges.filter((e) => e.type === "parent");
  const spouseEdges = edges.filter((e) => e.type === "spouse");

  return (
    <div
      ref={containerRef}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      data-testid="tree-canvas"
      className="relative overflow-hidden bg-[color:var(--bone)] cursor-grab active:cursor-grabbing select-none"
      style={{ minHeight: "68vh" }}
    >
      {/* Controls */}
      <div className="absolute top-3 right-3 z-20 flex flex-col gap-1 bg-[color:var(--bone)] border border-[color:var(--ink-line-strong)]">
        <button
          data-testid="tree-zoom-in"
          onClick={() => setScale((s) => Math.min(1.6, s + 0.15))}
          className="p-2 hover:bg-[color:var(--bone-2)]"
          title="Zoom in"
        >
          <ZoomIn size={14} />
        </button>
        <button
          data-testid="tree-zoom-out"
          onClick={() => setScale((s) => Math.max(0.3, s - 0.15))}
          className="p-2 hover:bg-[color:var(--bone-2)] border-t border-[color:var(--ink-line)]"
          title="Zoom out"
        >
          <ZoomOut size={14} />
        </button>
        <button
          data-testid="tree-fit"
          onClick={fit}
          className="p-2 hover:bg-[color:var(--bone-2)] border-t border-[color:var(--ink-line)]"
          title="Reset view"
        >
          <Locate size={14} />
        </button>
      </div>

      {/* Empty state */}
      {personsCount === 0 && (
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="overline mb-4 text-[color:var(--ink-soft)]">Blank Ledger</div>
            <div className="font-italic-serif italic text-2xl text-[color:var(--ink-soft)]">
              "A single name, and a whole century begins."
            </div>
          </div>
        </div>
      )}

      {/* Transformed layer */}
      <div
        className="absolute top-0 left-0"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "0 0",
          width: size.w,
          height: size.h,
        }}
      >
        {/* SVG connectors */}
        <svg
          width={size.w}
          height={size.h}
          className="absolute top-0 left-0 pointer-events-none"
        >
          {/* spouse: short horizontal line at mid-y between the two */}
          {spouseEdges.map((e) => {
            const a = nodes[e.from_person_id];
            const b = nodes[e.to_person_id];
            if (!a || !b) return null;
            const y = a.y + NODE_H / 2;
            return (
              <line
                key={`s-${e.id}`}
                x1={a.x + NODE_W}
                y1={y}
                x2={b.x}
                y2={y}
                stroke="#A0431E"
                strokeWidth="1.5"
                strokeDasharray="4 3"
              />
            );
          })}
          {/* parent -> child: L-shaped connector */}
          {parentEdges.map((e) => {
            const p = nodes[e.from_person_id];
            const c = nodes[e.to_person_id];
            if (!p || !c) return null;
            const startX = p.x + NODE_W / 2;
            const startY = p.y + NODE_H;
            const endX = c.x + NODE_W / 2;
            const endY = c.y;
            const midY = (startY + endY) / 2;
            const d = `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`;
            return (
              <path
                key={`p-${e.id}`}
                d={d}
                stroke="#1A1A1A"
                strokeOpacity="0.4"
                strokeWidth="1"
                fill="none"
              />
            );
          })}
        </svg>

        {/* Nodes */}
        {Object.values(nodes).map((n) => (
          <TreeNode
            key={n.id}
            node={n}
            selected={selectedId === n.id}
            onSelect={() => onSelect(n.id)}
            onAdd={(mode) => onAdd(mode, n.id)}
            width={NODE_W}
            height={NODE_H}
          />
        ))}
      </div>
    </div>
  );
}

function TreeNode({ node, selected, onSelect, onAdd, width, height }) {
  const fullName = [node.first_name, node.middle_name, node.last_name]
    .filter(Boolean)
    .join(" ");
  const life =
    [node.birth_date, node.death_date].filter(Boolean).length > 0
      ? `${node.birth_date || "?"} – ${node.is_living ? "" : node.death_date || "?"}`
      : "";
  const genderTint =
    node.gender === "female"
      ? "border-l-[3px] border-l-[color:var(--rust)]"
      : node.gender === "male"
      ? "border-l-[3px] border-l-[color:var(--ink)]"
      : "border-l-[3px] border-l-transparent";

  return (
    <div
      data-node
      data-testid={`tree-node-${node.id}`}
      onClick={onSelect}
      className={`absolute bg-[color:var(--bone)] border cursor-pointer group ${
        selected
          ? "border-[color:var(--rust)] shadow-[0_0_0_2px_rgba(156,66,33,0.15)]"
          : "border-[color:var(--ink-line-strong)]"
      } ${genderTint}`}
      style={{ left: node.x, top: node.y, width, height }}
    >
      <div className="flex h-full">
        <div className="w-14 border-r border-[color:var(--ink-line)] bg-[color:var(--bone-2)] flex items-center justify-center overflow-hidden">
          {node.photo_url ? (
            <img
              src={node.photo_url}
              alt={fullName}
              className="w-full h-full object-cover"
              style={{ filter: "grayscale(100%)" }}
            />
          ) : (
            <span className="font-serif-display text-lg text-[color:var(--ink-soft)]">
              {(node.first_name?.[0] || "•").toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 p-2 flex flex-col justify-between min-w-0">
          <div className="min-w-0">
            <div className="font-serif-display text-[15px] leading-tight truncate">
              {fullName || "Unnamed"}
            </div>
            {life && (
              <div className="text-[10px] text-[color:var(--ink-soft)] font-sans-ui mt-0.5 tabular-nums">
                {life}
              </div>
            )}
          </div>
          <div className="text-[9px] text-[color:var(--ink-soft)] overline truncate">
            {node.birth_place || (node.gender && node.gender !== "unknown" ? node.gender : "")}
          </div>
        </div>
      </div>

      {/* Quick-add hover strip */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 hidden group-hover:flex bg-[color:var(--ink)] text-[color:var(--bone)] text-[10px] gap-0">
        <button
          data-testid={`node-add-parent-${node.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onAdd("parent");
          }}
          className="px-2 py-1 hover:bg-[color:var(--rust)] inline-flex items-center gap-1"
          title="Add parent"
        >
          <ChevronUp size={10} /> parent
        </button>
        <button
          data-testid={`node-add-spouse-${node.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onAdd("spouse");
          }}
          className="px-2 py-1 hover:bg-[color:var(--rust)] inline-flex items-center gap-1 border-l border-[color:var(--bone)]/20"
          title="Add spouse"
        >
          <Heart size={10} /> spouse
        </button>
        <button
          data-testid={`node-add-child-${node.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onAdd("child");
          }}
          className="px-2 py-1 hover:bg-[color:var(--rust)] inline-flex items-center gap-1 border-l border-[color:var(--bone)]/20"
          title="Add child"
        >
          <ChevronDown size={10} /> child
        </button>
      </div>
    </div>
  );
}

/* ---------------- Side Panel ---------------- */

function PersonPanel({ person, editing, setEditing, onSave, onDelete, onAdd, relationships, persons }) {
  const [form, setForm] = useState(person);
  useEffect(() => setForm(person), [person.id]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const parentRels = relationships.filter((r) => r.type === "parent" && r.to_person_id === person.id);
  const childRels = relationships.filter((r) => r.type === "parent" && r.from_person_id === person.id);
  const spouseRels = relationships.filter(
    (r) => r.type === "spouse" && (r.from_person_id === person.id || r.to_person_id === person.id)
  );
  const nameOf = (pid) => {
    const p = persons.find((x) => x.id === pid);
    if (!p) return "?";
    return [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unnamed";
  };

  const fullName = [person.first_name, person.middle_name, person.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="p-6" data-testid={`person-panel-${person.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="overline text-[color:var(--ink-soft)] mb-1">Record</div>
          <h3 className="font-serif-display text-2xl leading-tight break-words">
            {fullName || "Unnamed"}
          </h3>
          {person.maiden_name && (
            <div className="font-italic-serif italic text-sm text-[color:var(--ink-soft)]">
              née {person.maiden_name}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button
            data-testid={`panel-edit-toggle-${person.id}`}
            onClick={() => setEditing((v) => !v)}
            className="p-2 border border-[color:var(--ink-line-strong)] hover:bg-[color:var(--bone-2)]"
            title={editing ? "Cancel" : "Edit"}
          >
            {editing ? <X size={14} /> : <Edit3 size={14} />}
          </button>
          <button
            data-testid={`panel-delete-${person.id}`}
            onClick={onDelete}
            className="p-2 border border-[color:var(--ink-line-strong)] hover:bg-[color:var(--rust)] hover:text-[color:var(--bone)]"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <PairField label="First" val={form.first_name} onChange={(v) => setF("first_name", v)} testid="edit-first" />
          <PairField label="Middle" val={form.middle_name || ""} onChange={(v) => setF("middle_name", v)} testid="edit-middle" />
          <PairField label="Last" val={form.last_name || ""} onChange={(v) => setF("last_name", v)} testid="edit-last" />
          <PairField label="Maiden name" val={form.maiden_name || ""} onChange={(v) => setF("maiden_name", v)} testid="edit-maiden" />
          <div>
            <div className="overline mb-1">Gender</div>
            <select
              data-testid="edit-gender"
              value={form.gender || "unknown"}
              onChange={(e) => setF("gender", e.target.value)}
              className="w-full bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)]"
            >
              <option value="unknown">Unknown</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <PairField label="Birth date" val={form.birth_date || ""} onChange={(v) => setF("birth_date", v)} placeholder="1934 or 1934-05-12" testid="edit-birth-date" />
          <PairField label="Birth place" val={form.birth_place || ""} onChange={(v) => setF("birth_place", v)} testid="edit-birth-place" />
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="edit-is-living"
              type="checkbox"
              checked={!!form.is_living}
              onChange={(e) => setF("is_living", e.target.checked)}
            />
            Living
          </label>
          {!form.is_living && (
            <>
              <PairField label="Death date" val={form.death_date || ""} onChange={(v) => setF("death_date", v)} testid="edit-death-date" />
              <PairField label="Death place" val={form.death_place || ""} onChange={(v) => setF("death_place", v)} testid="edit-death-place" />
            </>
          )}
          <div>
            <div className="overline mb-1">Photograph</div>
            <PhotoDropzone
              testid="edit-photo"
              value={form.photo_url || ""}
              onChange={(v) => setF("photo_url", v)}
            />
          </div>
          <div>
            <div className="overline mb-1">Chronicle</div>
            <MultilingualEditor
              as="textarea"
              rows={4}
              testid="edit-bio"
              value={form.bio || ""}
              onChange={(v) => setF("bio", v)}
              placeholder="A few lines about their life…"
            />
          </div>
          <button
            data-testid={`panel-save-${person.id}`}
            onClick={() => {
              onSave({ ...form, bio: packMl(form.bio) || null });
              setEditing(false);
            }}
            className="btn-rust w-full justify-center !py-2"
          >
            <Save size={14} /> Save changes
          </button>
        </div>
      ) : (
        <>
          {person.bio && (
            <MultilingualText
              value={person.bio}
              className="text-sm text-[color:var(--ink-soft)] leading-relaxed mb-4 font-italic-serif italic"
              testid={`panel-bio-${person.id}`}
            />
          )}
          <dl className="text-sm space-y-2 mb-6">
            {person.birth_date && <Row label="Born" v={`${person.birth_date}${person.birth_place ? " · " + person.birth_place : ""}`} />}
            {!person.is_living && person.death_date && (
              <Row label="Died" v={`${person.death_date}${person.death_place ? " · " + person.death_place : ""}`} />
            )}
            {person.gender && person.gender !== "unknown" && <Row label="Gender" v={person.gender} />}
          </dl>

          <RelBlock title="Parents" ids={parentRels.map((r) => r.from_person_id)} nameOf={nameOf} />
          <RelBlock title="Spouses" ids={spouseRels.map((r) => (r.from_person_id === person.id ? r.to_person_id : r.from_person_id))} nameOf={nameOf} />
          <RelBlock title="Children" ids={childRels.map((r) => r.to_person_id)} nameOf={nameOf} />

          <div className="mt-6 grid grid-cols-3 gap-0 border border-[color:var(--ink-line-strong)]">
            <button
              data-testid={`panel-add-parent-${person.id}`}
              onClick={() => onAdd("parent")}
              className="p-3 text-xs inline-flex flex-col items-center gap-1 hover:bg-[color:var(--bone-2)]"
            >
              <ChevronUp size={16} /> Parent
            </button>
            <button
              data-testid={`panel-add-spouse-${person.id}`}
              onClick={() => onAdd("spouse")}
              className="p-3 text-xs inline-flex flex-col items-center gap-1 border-l border-[color:var(--ink-line-strong)] hover:bg-[color:var(--bone-2)]"
            >
              <Heart size={16} /> Spouse
            </button>
            <button
              data-testid={`panel-add-child-${person.id}`}
              onClick={() => onAdd("child")}
              className="p-3 text-xs inline-flex flex-col items-center gap-1 border-l border-[color:var(--ink-line-strong)] hover:bg-[color:var(--bone-2)]"
            >
              <ChevronDown size={16} /> Child
            </button>
          </div>

          {/* Memorial page link — opens candle+guestbook page for this person */}
          <Link
            to={`/app/memorial/${person.id}`}
            data-testid={`panel-memorial-${person.id}`}
            className="mt-4 flex items-center justify-center gap-2 border border-[color:var(--rust)]/60 py-3 text-sm text-[color:var(--rust)] hover:bg-[color:var(--rust)] hover:text-[color:var(--parchment)] transition-colors duration-300"
          >
            <Flame size={14} />
            {person.is_living ? "Prepare memorial page" : "Open memorial page"}
          </Link>
        </>
      )}
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--ink-line)] pb-1">
      <dt className="overline text-[color:var(--ink-soft)]">{label}</dt>
      <dd className="text-right">{v}</dd>
    </div>
  );
}

function RelBlock({ title, ids, nameOf }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div className="mb-4">
      <div className="overline text-[color:var(--ink-soft)] mb-1">{title}</div>
      <ul className="text-sm">
        {ids.map((pid) => (
          <li key={pid} className="border-b border-[color:var(--ink-line)] py-1">
            {nameOf(pid)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PairField({ label, val, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <input
        data-testid={testid}
        value={val}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="w-full bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)]"
      />
    </div>
  );
}

/* ---------------- Add Person Modal ---------------- */

function AddPersonModal({ spec, anchorPerson, onClose, onCreate }) {
  const [form, setForm] = useState({ ...emptyPerson });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const title =
    spec.mode === "parent"
      ? `Add a parent of ${anchorPerson ? [anchorPerson.first_name, anchorPerson.last_name].filter(Boolean).join(" ") : ""}`
      : spec.mode === "child"
      ? `Add a child of ${anchorPerson ? [anchorPerson.first_name, anchorPerson.last_name].filter(Boolean).join(" ") : ""}`
      : spec.mode === "spouse"
      ? `Add a spouse of ${anchorPerson ? [anchorPerson.first_name, anchorPerson.last_name].filter(Boolean).join(" ") : ""}`
      : "Add a person to the archive";

  async function submit(e) {
    e.preventDefault();
    if (!form.first_name.trim()) return;
    let rel = null;
    if (spec.anchor && spec.mode === "parent") {
      rel = { type: "parent", from: "__new__", to: spec.anchor };
    } else if (spec.anchor && spec.mode === "child") {
      rel = { type: "parent", from: spec.anchor, to: "__new__" };
    } else if (spec.anchor && spec.mode === "spouse") {
      rel = { type: "spouse", from: spec.anchor, to: "__new__" };
    }
    await onCreate(
      {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
        maiden_name: form.maiden_name.trim() || undefined,
        gender: form.gender,
        birth_date: form.birth_date.trim() || undefined,
        birth_place: form.birth_place.trim() || undefined,
        death_date: form.is_living ? undefined : form.death_date.trim() || undefined,
        death_place: form.is_living ? undefined : form.death_place.trim() || undefined,
        is_living: !!form.is_living,
        bio: packMl(form.bio) || undefined,
        photo_url: form.photo_url.trim() || undefined,
      },
      rel
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-[color:var(--bone)] border border-[color:var(--ink)] w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="add-person-modal"
      >
        <header className="flex items-center justify-between p-6 border-b border-[color:var(--ink-line)] sticky top-0 bg-[color:var(--bone)]">
          <div>
            <div className="overline mb-1">New record</div>
            <h3 className="font-serif-display text-2xl leading-tight">{title}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="add-person-close" className="p-2 hover:bg-[color:var(--bone-2)]">
            <X size={16} />
          </button>
        </header>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <PairField label="First name *" val={form.first_name} onChange={(v) => setF("first_name", v)} testid="add-first-name" />
          <PairField label="Middle name" val={form.middle_name} onChange={(v) => setF("middle_name", v)} testid="add-middle-name" />
          <PairField label="Last name" val={form.last_name} onChange={(v) => setF("last_name", v)} testid="add-last-name" />
          <PairField label="Maiden name" val={form.maiden_name} onChange={(v) => setF("maiden_name", v)} testid="add-maiden-name" />
          <div>
            <div className="overline mb-1">Gender</div>
            <select
              data-testid="add-gender"
              value={form.gender}
              onChange={(e) => setF("gender", e.target.value)}
              className="w-full bg-transparent border-b border-[color:var(--ink)] py-2 focus:outline-none focus:border-[color:var(--rust)]"
            >
              <option value="unknown">Unknown</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <PairField label="Birth date" val={form.birth_date} onChange={(v) => setF("birth_date", v)} placeholder="1934 or 1934-05-12" testid="add-birth-date" />
          <PairField label="Birth place" val={form.birth_place} onChange={(v) => setF("birth_place", v)} testid="add-birth-place" />
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              data-testid="add-is-living"
              type="checkbox"
              checked={form.is_living}
              onChange={(e) => setF("is_living", e.target.checked)}
            />
            Living
          </label>
          {!form.is_living && (
            <>
              <PairField label="Death date" val={form.death_date} onChange={(v) => setF("death_date", v)} testid="add-death-date" />
              <PairField label="Death place" val={form.death_place} onChange={(v) => setF("death_place", v)} testid="add-death-place" />
            </>
          )}
          <div className="md:col-span-2">
            <div className="overline mb-1">Photograph</div>
            <PhotoDropzone
              testid="add-photo"
              value={form.photo_url}
              onChange={(v) => setF("photo_url", v)}
            />
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-1">Chronicle</div>
            <MultilingualEditor
              as="textarea"
              rows={3}
              testid="add-bio"
              value={form.bio}
              onChange={(v) => setF("bio", v)}
              placeholder="A few lines about their life…"
            />
          </div>
        </div>
        <footer className="p-6 border-t border-[color:var(--ink-line)] flex items-center justify-between sticky bottom-0 bg-[color:var(--bone)]">
          <div className="text-xs text-[color:var(--ink-soft)]">
            {spec.anchor
              ? "A relationship will be recorded automatically."
              : "This person will start without any connections. You can link them later."}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4">
              Cancel
            </button>
            <button data-testid="add-person-submit" type="submit" className="btn-rust !py-2 !px-5">
              <UserPlus size={14} /> Add to archive
            </button>
          </div>
        </footer>
      </motion.form>
    </motion.div>
  );
}
